import styled from "styled-components";
import { designSystemVars as vars } from "../tokens";
import { ButtonBase } from "./Button";

export type NavigationStepStatus =
  | "active"
  | "completed"
  | "upcoming"
  | "lastAvailable";

export const SideNavigation = styled.div<{ $collapsed?: boolean }>`
  position: sticky;
  top: 20px;
  display: flex;
  width: ${({ $collapsed }) => ($collapsed ? "84px" : "225px")};
  min-width: ${({ $collapsed }) => ($collapsed ? "84px" : "225px")};
  height: fit-content;
  flex-shrink: 0;
  flex-direction: column;
  align-items: flex-start;
  gap: 0;
  overflow: visible;
  box-sizing: border-box;
  border-radius: ${vars.radius.xl};
  background: ${vars.color.background.surface};
  box-shadow: ${vars.shadow.dropdown};
`;

export const SideNavigationContent = styled.div<{ $collapsed?: boolean }>`
  position: relative;
  display: flex;
  width: 100%;
  box-sizing: border-box;
  flex-shrink: 0;
  flex-direction: column;
  align-items: ${({ $collapsed }) => ($collapsed ? "center" : "flex-end")};
  gap: 0;
  overflow: visible;
  padding: ${({ $collapsed }) =>
    $collapsed
      ? "var(--inzeloop-ds-spacing-xs) calc(var(--inzeloop-ds-spacing-20px) + var(--inzeloop-ds-spacing-1px)) 0"
      : "var(--inzeloop-ds-spacing-xs) var(--inzeloop-ds-spacing-xs) 0 var(--inzeloop-ds-spacing-xl)"};

  @media (min-width: 834px) and (max-width: 1680px) {
    width: ${({ $collapsed }) => ($collapsed ? "100%" : "225px")};
  }
`;

export const SideNavigationCollapsePosition = styled.div<{
  $collapsed?: boolean;
  $empty?: boolean;
}>`
  position: absolute;
  top: ${({ $collapsed, $empty }) =>
    $collapsed && $empty ? "18%" : $collapsed ? "10px" : "0"};
  right: ${({ $collapsed }) => ($collapsed ? "50%" : "0")};
  z-index: 1;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: ${({ $collapsed }) =>
    $collapsed ? "var(--inzeloop-ds-spacing-xxs)" : "0"};
  transform: ${({ $collapsed }) => ($collapsed ? "translateX(50%)" : "none")};
`;

export const SideNavigationCollapseControl = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${vars.spacing.xs};
  padding: ${vars.spacing.xxs};
`;

export const SideNavigationCollapseButton = styled(ButtonBase)`
  display: flex;
  align-self: stretch;
  align-items: center;
  justify-content: center;
  gap: ${vars.spacing.xs};
  padding: ${vars.spacing.sm};
  border: 0;
  background: var(--inzeloop-ds-color-background-transparent);
  color: ${vars.color.text.primary};

  &:focus-visible {
    outline: none;
    border-radius: ${vars.radius.sm};
    box-shadow: ${vars.shadow.focus};
  }

  &:hover {
    border-radius: ${vars.radius.xs};
    background: ${vars.color.action.primaryHover};
    color: ${vars.color.text.inverse};
  }
`;

export const SideNavigationItems = styled.div<{
  $collapsed?: boolean;
  $empty?: boolean;
  $isSetupCollapsed?: boolean;
}>`
  display: flex;
  width: 100%;
  align-self: stretch;
  box-sizing: border-box;
  flex-direction: column;
  align-items: flex-start;
  gap: ${({ $empty, $isSetupCollapsed }) =>
    $empty || $isSetupCollapsed ? "0" : vars.spacing.lg};
  overflow: visible;
  padding: ${({ $collapsed }) =>
    $collapsed ? `50px 0 ${vars.spacing.xl}` : `${vars.spacing.xl} 0`};
  margin-top: ${({ $collapsed, $empty }) =>
    $collapsed && !$empty ? "var(--inzeloop-ds-spacing-sm)" : "0"};
  cursor: default;
`;

export const SideNavigationItem = styled.div<{ $active?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: ${vars.spacing.sm};
  padding: ${vars.spacing.xs};
  border-bottom: ${({ $active }) =>
    $active
      ? `${vars.borderWidth.medium} solid ${vars.color.action.primaryPressed}`
      : "none"};
  border-radius: ${vars.radius.xs} ${vars.radius.xs} 0 0;
  color: ${({ $active }) =>
    $active ? vars.color.action.primaryPressed : vars.color.text.primary};
  cursor: default;

  &:hover {
    border-radius: ${vars.radius.xs};
    background: ${vars.color.action.primaryHover};
    color: ${vars.color.text.inverse};
  }

  &:focus-visible {
    outline: none;
    border: 0;
    border-radius: ${vars.radius.sm};
    box-shadow: ${vars.shadow.focus};
  }
`;

export const SideNavigationItemContent = styled.div`
  display: flex;
  height: 36px;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  gap: 0;
  font-family: ${vars.typography.family.sans};
  font-size: ${vars.typography.size.sm};
  font-weight: ${vars.typography.weight.regular};
`;

export const SideNavigationSection = styled.div<{
  $isSectionCollapsed?: boolean;
}>`
  display: grid;
  grid-template-columns: ${vars.spacing.px60} ${vars.spacing.xl};
  width: fit-content;
  height: 34px;
  box-sizing: border-box;
  align-self: flex-start;
  flex-shrink: 0;
  align-items: center;
  justify-content: flex-start;
  gap: var(--inzeloop-ds-spacing-10px);
  padding: 0 ${vars.spacing.sm};
  color: ${vars.color.text.primary};

  &:hover {
    border-radius: ${vars.radius.xs};
    background: ${vars.color.action.primary};
    color: ${vars.color.text.inverse};
  }

  &:focus-visible {
    outline: none;
    border-radius: ${vars.radius.xs};
    box-shadow: ${vars.shadow.focus};
  }
`;

export const SideNavigationDropdownControl = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${vars.spacing.xs};
`;

export const StepMenuItem = styled(SideNavigationItem)<{
  $status?: NavigationStepStatus;
  disabled?: boolean;
}>`
  cursor: ${({ $status }) =>
    $status === "upcoming" ? "not-allowed" : "pointer"};

  &:hover {
    background: none;
    color: ${vars.color.text.primary};
  }
`;

export const StepMenuItemContent = styled(SideNavigationItemContent)<{
  $status?: NavigationStepStatus;
  $active?: boolean;
}>`
  height: auto;
  min-height: 36px;
  border-bottom: ${({ $active }) =>
    $active
      ? `${vars.borderWidth.medium} solid ${vars.color.action.primaryPressed}`
      : "none"};
  color: ${({ $status }) =>
    $status === "upcoming"
      ? vars.color.text.disabled
      : vars.color.text.primary};
  font-weight: ${({ $active }) =>
    $active ? vars.typography.weight.semibold : vars.typography.weight.regular};
  line-height: ${vars.typography.lineHeight.xs};

  ${({ $status }) =>
    $status !== "upcoming" &&
    `
      &:hover {
        border-radius: ${vars.radius.xs};
        background: ${vars.color.action.primaryHover};
        color: ${vars.color.text.inverse};
      }
    `}
`;

export const StepMenu = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

export const StepMenuLine = styled.div<{ $lineColor?: string }>`
  position: absolute;
  top: 88%;
  left: 20px;
  z-index: 0;
  width: 2px;
  height: 30px;
  background: ${({ $lineColor }) =>
    $lineColor || "var(--inzeloop-ds-color-background-hover)"};
`;
